/** Find one of our records to link their id to. */

import { useState } from "react";
import { Link2 } from "lucide-react";

import { Button, MenuItem, MenuLabel, SearchField, Select } from "@/components/kit";
import { Floating } from "@/components/kit/Floating";
import { searchRecords, type LinkEntity, type RecordHit } from "@/lib/integrations/api";

import { useDebounced, useKeyed } from "./hooks";

export const ENTITY_WORDS: Record<LinkEntity, { one: string; many: string }> = {
  sport: { one: "sport", many: "Sports" },
  participant: { one: "player or team", many: "Players and teams" },
  venue: { one: "venue", many: "Venues" },
  fixture: { one: "fixture", many: "Fixtures" },
};

export function RecordPicker({
  code,
  entity: startEntity,
  label = "Pick a record",
  lockEntity = false,
  disabled = false,
  busy = false,
  onPick,
}: {
  code: string;
  entity: LinkEntity;
  label?: string;
  lockEntity?: boolean;
  disabled?: boolean;
  busy?: boolean;
  onPick: (hit: RecordHit, entity: LinkEntity) => void;
}) {
  const [open, setOpen] = useState(false);
  const [entity, setEntity] = useState<LinkEntity>(startEntity);
  const [search, setSearch] = useState("");
  const typed = useDebounced(search, 300);
  const hits = useKeyed(open ? `${code}|${entity}|${typed}` : null, () => searchRecords(code, { entity, q: typed, limit: 20 }));
  return (
    <Floating
      open={open}
      onClose={() => setOpen(false)}
      wide
      trigger={
        <Button onClick={() => setOpen(!open)} disabled={disabled || busy}>
          <Link2 size={15} aria-hidden />
          {busy ? "Linking…" : label}
        </Button>
      }
    >
      <div style={{ display: "grid", gap: "var(--s2)", width: "min(26rem, 80vw)" }}>
        {!lockEntity && (
          <Select aria-label="Kind of record" value={entity} onChange={(event) => setEntity(event.target.value as LinkEntity)}>
            {(Object.keys(ENTITY_WORDS) as LinkEntity[]).map((one) => (
              <option key={one} value={one}>
                {ENTITY_WORDS[one].many}
              </option>
            ))}
          </Select>
        )}
        <SearchField
          autoFocus
          value={search}
          placeholder={`Find a ${ENTITY_WORDS[entity].one} by name or code`}
          aria-label="Find a record"
          onChange={(event) => setSearch(event.target.value)}
        />
        <MenuLabel>{hits.busy ? "Looking…" : hits.error ? "Could not look" : "Pick one"}</MenuLabel>
        {hits.error && <p style={{ margin: 0, color: "var(--bad)" }}>{hits.error}</p>}
        <div style={{ maxHeight: "18rem", overflow: "auto" }}>
          {(hits.data ?? []).length === 0 && !hits.busy && !hits.error && (
            <MenuItem label="Nothing found" onClick={() => undefined} disabled />
          )}
          {(hits.data ?? []).map((hit) => (
            <MenuItem
              key={hit.id}
              label={hit.name ?? hit.code ?? hit.id}
              kind={[hit.code, hit.note].filter(Boolean).join(" · ")}
              onClick={() => {
                setOpen(false);
                onPick(hit, entity);
              }}
            />
          ))}
        </div>
      </div>
    </Floating>
  );
}
